import classNames from 'classnames'
import { LoadingIcon } from 'src/components/Icons/LoadingIcon'
import type { ProgressiveDiscountTier } from 'src/components/restructure/product/types/product'
import { getActiveProgressiveTier } from 'src/components/restructure/product/utils/progressiveDiscountPricing'
import { formatterPrice } from 'src/utils/formatterPrice'

interface ProgressiveDiscountTiersProps {
  tiers: ProgressiveDiscountTier[]
  quantity: number
  isLoading?: boolean
  onSelectTier: (quantity: number) => void
}

function getNudge(tiers: ProgressiveDiscountTier[], quantity: number) {
  if (!tiers.length) {
    return null
  }

  const sortedTiers = [...tiers].sort((a, b) => a.quantity - b.quantity)
  const [baseTier] = sortedTiers
  const lastTier = sortedTiers[sortedTiers.length - 1]

  if (quantity >= lastTier.quantity) {
    const savings =
      (baseTier.unitPrice - lastTier.unitPrice) * Math.max(quantity, 0)

    return {
      isMaximum: true,
      message: `Desconto máximo ativo! você economiza ${formatterPrice(
        savings / 100
      )}`,
    }
  }

  return {
    isMaximum: false,
    message: 'Compre mais e economize: quanto mais, maior o desconto.',
  }
}

export function ProgressiveDiscountTiers({
  tiers,
  quantity,
  isLoading = false,
  onSelectTier,
}: ProgressiveDiscountTiersProps) {
  const sortedTiers = [...tiers].sort((a, b) => a.quantity - b.quantity)
  const activeTier = getActiveProgressiveTier(sortedTiers, quantity)
  const nudge = getNudge(sortedTiers, quantity)

  if (!sortedTiers.length) {
    return null
  }

  return (
    <div
      className="flex w-full flex-col gap-4"
      aria-busy={isLoading}
      data-testid="progressive-discount-tiers"
    >
      <div className="relative flex w-full border-y border-l border-restructure-border-secondary">
        {sortedTiers.map((tier) => {
          const isActive = activeTier?.quantity === tier.quantity
          const quantityLabel =
            tier.quantity === 1 ? '1 item' : `${tier.quantity} itens`

          const discountLabel =
            tier.discountPercentage > 0
              ? `${tier.discountPercentage}% OFF`
              : '—'

          return (
            <button
              key={tier.quantity}
              type="button"
              aria-label={`${quantityLabel}, ${discountLabel}, ${formatterPrice(
                tier.unitPrice / 100
              )} por unidade`}
              aria-pressed={isActive}
              className={classNames(
                'relative flex h-[88px] min-w-0 flex-1 flex-col items-center justify-center gap-[3px] border-r border-restructure-border-secondary px-2 py-[10px] text-center transition-colors focus-visible:z-10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-restructure-focus-primary',
                isLoading ? 'disabled:cursor-wait' : 'disabled:cursor-default',
                isActive
                  ? 'bg-restructure-background-action-1 text-restructure-primary-inverted'
                  : 'bg-white text-restructure-primary'
              )}
              disabled={isLoading || tier.quantity === quantity}
              onClick={() => onSelectTier(tier.quantity)}
            >
              <span
                className={classNames(
                  'whitespace-nowrap text-[11px] font-regular leading-none',
                  isActive
                    ? 'text-restructure-secondary-inverted'
                    : 'text-restructure-primary'
                )}
              >
                {quantityLabel}
              </span>
              <span className="whitespace-nowrap text-base font-bold leading-none">
                {discountLabel}
              </span>
              <span
                className={classNames(
                  'whitespace-nowrap text-font-size-2 font-medium',
                  isActive
                    ? 'text-restructure-secondary-inverted'
                    : 'text-restructure-tertiary'
                )}
              >
                {formatterPrice(tier.unitPrice / 100)}/un
              </span>

              {tier.bestOption && (
                <span className="absolute -top-[11px] right-0 flex h-[22px] items-center bg-yellow-300 px-1 py-0.5 text-font-size-2 font-medium text-restructure-primary">
                  Melhor
                </span>
              )}
            </button>
          )
        })}

        {isLoading && (
          <div
            className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center bg-white/50"
            role="status"
            aria-live="polite"
            data-testid="progressive-discount-loading"
          >
            <LoadingIcon />
          </div>
        )}
      </div>

      {nudge && (
        <p
          className={classNames(
            'min-h-[18px] text-font-size-2 font-medium',
            nudge.isMaximum
              ? 'text-restructure-success'
              : 'text-restructure-action'
          )}
        >
          {nudge.message}
        </p>
      )}
    </div>
  )
}
